// Stable identifier for anonymous (shared-link) dialog respondents. Keyed
// per dialog token so two dialogs opened in the same browser don't collide,
// and persisted in localStorage so a refresh / browser crash doesn't reroll
// the id (which is what breaks AgentCore Memory's actorId scoping).
//
// Invited respondents don't use this — they key on respondent.id, which is
// stable across browsers. This utility is purely the anonymous path.

const STORAGE_PREFIX = 'valuegraph-client-session-id:';

// Roll a fresh session id for a token, persisting it so subsequent reads
// resolve the new (empty) session. Used by the idea coach's "Start over" — a
// new actorId means AgentCore Memory has no events for it, so the conversation
// begins fresh while the dialog/token stay the same. The prior session's
// events are simply orphaned under the old actorId.
export function resetClientSessionId(token: string): string {
  const fresh = crypto.randomUUID();
  try {
    window.localStorage.setItem(STORAGE_PREFIX + token, fresh);
  } catch {
    // localStorage blocked — the returned id still drives this session; it
    // just won't survive a reload (same trade-off as getOrCreate's fallback).
  }
  return fresh;
}

export function getOrCreateClientSessionId(token: string): string {
  const key = STORAGE_PREFIX + token;
  // Defensive against localStorage being blocked (private browsing in some
  // configurations, sandboxed iframes, or the window/localStorage globals
  // being undefined in a non-browser test runner). The try/catch swallows
  // both ReferenceError (window undefined) and any storage exceptions —
  // we fall back to a per-call UUID. Persistence won't survive a reload,
  // but the conversation works as it did before.
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}
